import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop.tsx';
import Layout from '@/components/layout/layout';
import { ROUTES } from '@/config/routes';
import { useHydration } from '@/hooks/useHydration';
import { getSaasParams } from '@/lib/api/saasApi';
import APropos from '@/pages/a-propos';
import Article from '@/pages/article';
import AuthCallback from '@/pages/auth-callback';
import Blog from '@/pages/blog';
import CguCgv from '@/pages/cgu-cgv';
import ClubProfile from '@/pages/club-profile';
// Annuaire public
import Clubs from '@/pages/clubs';
import CommentCaMarche from '@/pages/comment-ca-marche';
import Concour from '@/pages/concour';
import ConcoursResultats from '@/pages/concours-resultats';
import Contact from '@/pages/contact';
import Cookies from '@/pages/cookies';
import Demo from '@/pages/demo';
import DemoClub from '@/pages/demo-club.tsx';
import DemoOrganizer from '@/pages/demo-organizer.tsx';
import EntityBlog from '@/pages/entity-blog';
import Fonctionnalites from '@/pages/fonctionnalites';
import ForgotPassword from '@/pages/forgot-password';
// Public vitrine
import Home from '@/pages/home';
import ImpersonateCallback from '@/pages/impersonate-callback';
import InscriptionsFermees from '@/pages/inscriptions-fermees';
// Auth
import Login from '@/pages/login';
import Maintenance from '@/pages/maintenance';
import MemberProfile from '@/pages/member-profile';
// Légal
import MentionsLegales from '@/pages/mentions-legales';
import MonEspace from '@/pages/mon-espace';
// 404
import NotFound from '@/pages/not-found';
import OrganizationProfile from '@/pages/organization-profile';
import Parrainage from '@/pages/parrainage';
// import ParrainageClub from '@/pages/parrainage-club'; // Masqué V1-A — pas de backend
// Parrainage
import ParrainageIndex from '@/pages/parrainage-index';
// import ParrainageJoueur from '@/pages/parrainage-joueur'; // Masqué V1-A — pas de backend
import Plv from '@/pages/plv';
import PolitiqueRgpd from '@/pages/politique-rgpd';
import Register from '@/pages/register';
import RegisterConcour from '@/pages/register-concour.tsx';
import SearchConcours from '@/pages/search-concours';
import SearchOrganizations from '@/pages/search-organizations';
import SiteWeb from '@/pages/site-web';
import Support from '@/pages/support';
import Tarifs from '@/pages/tarifs';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { Role } from '@shared';

// Synchronise la session après réhydratation du localStorage.
// - Authentifié localement : rafraîchit les données user depuis /users/me
// - Non authentifié : tente une restauration silencieuse via le cookie refresh token
//   (permet le SSO entre le front public et le backoffice admin)
function SessionSync() {
  const hydrated = useHydration();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const markSessionChecked = useAuthStore(s => s.markSessionChecked);
  const refreshSession = useAuthStore(s => s.refreshSession);
  const tryRestoreSession = useAuthStore(s => s.tryRestoreSession);

  useEffect(() => {
    if (!hydrated) return;
    if (isAuthenticated) {
      // Données déjà en store — marquer prêt immédiatement, sync en arrière-plan
      markSessionChecked();
      refreshSession();
    } else {
      // Tenter de restaurer via le cookie refresh (SSO cross-app)
      // tryRestoreSession marque sessionChecked dans son finally
      tryRestoreSession();
    }
  }, [hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// Fetches SaaS params once on mount and populates the saas-store.
// Uses markLoaded() in the catch branch so `loaded` always becomes true
// even if the API is unreachable (fail-open: site stays accessible).
function SaasParamsSync() {
  const setParams = useSaasStore(s => s.setParams);
  const markLoaded = useSaasStore(s => s.markLoaded);

  useEffect(() => {
    getSaasParams()
      .then(params => setParams(params))
      .catch(() => markLoaded());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// Redirects every route to /maintenance unless:
//   - current path is /maintenance itself (avoid redirect loop)
//   - current path is /connexion (SUPER_ADMIN needs to be able to log in)
//   - the user is a SUPER_ADMIN (bypass)
function MaintenanceGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const loaded = useSaasStore(s => s.loaded);
  const maintenanceModeFront = useSaasStore(s => s.maintenanceModeFront);
  const hasRole = useAuthStore(s => s.hasRole);

  // While params are loading, render nothing (avoids flash of maintenance page)
  if (!loaded) {
    return null;
  }

  const isMaintenance = maintenanceModeFront;
  const isSuperAdmin = hasRole(Role.SUPER_ADMIN);
  const isExemptPath = location.pathname === ROUTES.MAINTENANCE || location.pathname === ROUTES.LOGIN;

  if (isMaintenance && !isSuperAdmin && !isExemptPath) {
    return <Navigate to={ROUTES.MAINTENANCE} replace />;
  }

  return <>{children}</>;
}

// Redirects parrainage routes to home when referralEnabled is false.
function ReferralGuard({ children }: { children: React.ReactNode }) {
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const loaded = useSaasStore(s => s.loaded);

  if (!loaded) return null;
  if (!referralEnabled) return <Navigate to={ROUTES.HOME} replace />;

  return <>{children}</>;
}

// Redirects /inscription to /inscriptions-fermees when openRegister is false.
// function RegisterGuard({ children }: { children: React.ReactNode }) {
//   const openRegister = useSaasStore(s => s.openRegister);
//
//   if (!openRegister) {
//     return <Navigate to={ROUTES.INSCRIPTIONS_FERMEES} replace />;
//   }
//
//   return <>{children}</>;
// }

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
        <SessionSync />
        <SaasParamsSync />
        <MaintenanceGuard>
          <Routes>
            {/* Callback SSO — hors Layout pour éviter navbar/footer */}
            <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />
            <Route path={ROUTES.IMPERSONATE_CALLBACK} element={<ImpersonateCallback />} />

            {/* Page maintenance — hors Layout, toujours accessible */}
            <Route path={ROUTES.MAINTENANCE} element={<Maintenance />} />

            <Route element={<Layout />}>
              {/* Public vitrine */}
              <Route path={ROUTES.HOME} element={<Home />} />
              <Route path={ROUTES.FONCTIONNALITES} element={<Fonctionnalites />} />
              <Route path={ROUTES.FONCTIONNALITES_SITE_WEB} element={<SiteWeb />} />
              <Route path={ROUTES.TARIFS} element={<Tarifs />} />
              <Route path={ROUTES.CONTACT} element={<Contact />} />
              <Route path={ROUTES.BLOG} element={<Blog />} />
              <Route path={ROUTES.ARTICLE} element={<Article />} />
              <Route path={ROUTES.COMMENT_CA_MARCHE} element={<CommentCaMarche />} />
              <Route path={ROUTES.SUPPORT} element={<Support />} />
              <Route path={ROUTES.DEMO} element={<Demo />} />
              <Route path={ROUTES.PLV} element={<Plv />} />
              <Route path={ROUTES.DEMO_CLUB} element={<DemoClub />} />
              <Route path={ROUTES.DEMO_ORGANIZER} element={<DemoOrganizer />} />

              {/* Annuaire public */}
              <Route path={ROUTES.CLUBS} element={<Clubs />} />
              <Route path={ROUTES.CLUB_PROFILE} element={<ClubProfile />} />
              <Route path={ROUTES.CLUB_BLOG} element={<EntityBlog scope='club' />} />
              <Route path={ROUTES.SEARCH_CONCOURS} element={<SearchConcours />} />
              <Route path={ROUTES.CONCOUR} element={<Concour />} />
              <Route
                path={ROUTES.REGISTER_CONCOUR}
                element={
                  // <RegisterGuard>
                  <RegisterConcour />
                  // </RegisterGuard>
                }
              />
              <Route path={ROUTES.CONCOURS_RESULTATS} element={<ConcoursResultats />} />
              <Route path={ROUTES.SEARCH_ORGANIZATIONS} element={<SearchOrganizations />} />
              <Route path={ROUTES.ORGANIZATION_PROFILE} element={<OrganizationProfile />} />
              <Route path={ROUTES.ORGANIZATION_BLOG} element={<EntityBlog scope='organization' />} />

              {/* Parrainage (masqué si referralEnabled=false dans SaasParams) */}
              <Route
                path={ROUTES.PARRAINAGE}
                element={
                  <ReferralGuard>
                    <ParrainageIndex />
                  </ReferralGuard>
                }
              />
              <Route
                path={ROUTES.PARRAINAGE_ORGANISATEUR}
                element={
                  <ReferralGuard>
                    <Parrainage />
                  </ReferralGuard>
                }
              />
              <Route path={ROUTES.PARRAINAGE_CLUB} element={<Navigate to={ROUTES.PARRAINAGE} replace />} />
              <Route path={ROUTES.PARRAINAGE_JOUEUR} element={<Navigate to={ROUTES.PARRAINAGE} replace />} />

              {/* Auth */}
              <Route path={ROUTES.LOGIN} element={<Login />} />
              <Route path={ROUTES.REGISTER} element={<Register />} />
              <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />

              {/* Inscriptions fermées */}
              <Route path={ROUTES.INSCRIPTIONS_FERMEES} element={<InscriptionsFermees />} />

              {/* Espace personnel (authentifié) */}
              <Route path={ROUTES.ROLE_LANDING} element={<MonEspace />} />

              {/* Joueur */}
              <Route path={ROUTES.MEMBER_PROFILE} element={<MemberProfile />} />

              {/* Légal */}
              <Route path={ROUTES.MENTIONS_LEGALES} element={<MentionsLegales />} />
              <Route path={ROUTES.CGU_CGV} element={<CguCgv />} />
              <Route path={ROUTES.POLITIQUE_RGPD} element={<PolitiqueRgpd />} />
              <Route path={ROUTES.COOKIES} element={<Cookies />} />
              <Route path={ROUTES.A_PROPOS} element={<APropos />} />

              {/* 404 */}
              <Route path='*' element={<NotFound />} />
            </Route>
          </Routes>
        </MaintenanceGuard>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
